<template>
  <div
    class="grid"
  >
    <div
      v-if="computedShowList"
      class="grid__headers"
    >
      <ol
        ref="headers"
        :style="computedColumns"
        class="grid__row grid__row--headers"
      >
        <li
          v-for="header in computedHeaders"
          :key="header"
          class="grid__row__column"
        >
          <b>
            {{ header }}
          </b>
        </li>
      </ol>
    </div>

    <div
      v-if="computedShowList"
      ref="content"
      class="grid__content"
    >
      <ol
        v-for="(row, index) in computedList"
        :key="index"
        :style="computedColumns"
        class="grid__row"
      >
        <li
          v-for="(column, key) in row"
          :key="key"
          class="grid__row__column"
          v-html="formatCell(key, column)"
        />
      </ol>
    </div>

    <div
      v-if="computedList !== null && !computedColumns"
      class="no-data"
    >
      {{ $t('messages.sharedTexts.status.noData') }}
      <font-awesome-layers>
        <font-awesome-icon :icon="['fas', 'database']" />
        <font-awesome-icon
          :icon="['fas', 'slash']"
          transform="left-2"
        />
      </font-awesome-layers>
    </div>
    
    <div
      v-if="computedList === null"
      class="g-count-loader"
    >
      <span>{{ $t('messages.sharedTexts.status.loading') }}<hr>{{ $t('messages.sharedTexts.takeTime') }}</span>
    </div>
  </div>
</template>

<script>
import {FontAwesomeIcon, FontAwesomeLayers} from '@fortawesome/vue-fontawesome'

export default {
  name: 'GridElement',
  components: {
    'font-awesome-icon': FontAwesomeIcon,
    'font-awesome-layers': FontAwesomeLayers
  },
  props: {
    list: { // Be aware of the difference between the displayed computedList and List operations. They're not interchangeable.
      type: Array,
      default() {
        return [{
          year: "2019",
          month: "2",
          day: "28",
          gal_day: "0",
          exp_day: "0",
          unsched_day: "0"
        }]
      }
    },
    formatTable: {
      type: Object,
      default() {
        return {}
      }
    },
    reverse: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      errorOut: false
    }
  },
  computed: {
    computedHeaders() {
      return Object.keys(this.computedList[0]).map(header => this.formatCellHeader(header))
    },
    computedList() {
      return this.reverse
      ? this.list.slice().reverse() // non-mutating
      : this.list
    },
    computedShowList() {
      return this.list && Array.isArray(this.list) && this.list.length && this.formatTable
    },
    computedColumns() {
      if(this.list && this.list.length) {
        const columns = Object.keys(this.list[0]).length
        const minColumns = 11
        const actualColumns = columns > minColumns ? columns : minColumns
        return `grid-template-columns: repeat(${actualColumns}, 86px)`
      } else {
        return false
      }
    }
  },
  watch: {
    computedShowList() {
      this.$nextTick(() => {
        let content = this.$refs.content
        let headers = this.$refs.headers

        if(content && headers)
          content.addEventListener('scroll', () => headers.scrollLeft = content.scrollLeft)
      })
    }
  },
  mounted(){
  },
  methods: {
    getTableAsCSV(delimiter, quotes) {
      const list = this.computedList.map(listItem =>
        Object.keys(listItem).reduce((acc, key) =>
          [...acc, quotes + this.formatCell(key, listItem[key]).replace(/<[^>]*>?/gm, '-') + quotes]  // added .replace() to strip out <b>..</b> tags from History cell data; using '-' to denote another level of importance for the cell contents (like a subheading) - jimm 23.feb.2021
        , []).join(delimiter)
      ).join('\n')

      return this.computedHeaders.join(delimiter) + '\n' + list + '\n'
    },
    formatCellHeader(key) {
      if(!((this.formatTable && Object) && typeof key === 'string'))
        return key

      if(this.formatTable[key] === undefined) {
        this.errorOut = true
        return console.error(key, 'not found in formatTable (headers)')
      }

      return this.formatTable[key].label
      ? this.formatTable[key].label
      : key
    },
    formatCell(key, value) {
      if(this.errorOut) return
      if(!((this.formatTable && Object) && typeof key === 'string'))
        return value
      
      if(!this.formatTable[key] === undefined) {
        this.errorOut = true
        return console.error(key, 'not found in formatTable')
      }
      
      const conversion = this.formatTable[key].valueConversion

      let val = typeof conversion === 'function'
      ? conversion(value)
      : value

      return val
    }
  }
}
</script>

<style lang="scss">
div.g-count-loader span {
  font-size: 10px;
  text-align: center;

  hr {
    border-color: $color-grey;
    border-top: 0px;
  }
}

.grid {
  $grid-color: transparent;
  font-size: 12px;
  margin-top: 10px;

  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  position: relative;

  .no-data {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    font: $font-primary-medium;
    text-align: center;
    font-size: 22px;
    color: $color-grey;
  }

  &__headers {
    overflow: hidden;
    background-color: darken($color-grey-light, 12) !important;
    padding-right: 17px;

  }
  &__content {
    overflow: auto;
  }

  &__row {
    display: grid;
    border-left: 1px solid $grid-color;
    word-wrap: break-word;
    background: $color-white;

    &:nth-child(even) {
      background-color: darken($color-grey-light, 5);
      > * { // Issues in FF with bg color on non-visible rows, applying to cells also
        background-color: darken($color-grey-light, 5);
      }
    }

    &:first-child {
      border-top: 1px solid $grid-color;
    }

    &:last-child {
      box-shadow: 0 5px 4px -4px $color-grey;
    }

    &__column  {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border-bottom: 1px solid $grid-color;
      border-right: 1px solid $grid-color;
      padding: 5px 7px;
      text-align: center;
    }

    &--headers {
      background-color: transparent !important;
      overflow-x: hidden;
      pointer-events: none;

       > li {
         width: 100%;
       }
    }
  }
}
</style>
